import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout.js';
import Section from '../../common/Section.js';
import Button from '../../common/Button.js';
import styled from 'styled-components';
import { FaMapMarkerAlt } from 'react-icons/fa';

// Hero section for the LandDevelopment page
const Hero = styled.div`
  position: relative;
  background-color: var(--primary);
  color: white;
  padding: 4rem 0;
  margin-bottom: 3rem;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url('/images/hero2.png');
    background-size: cover;
    background-position: center;
    opacity: 0.3;
    z-index: 0;
  }
  
  .container {
    position: relative;
    z-index: 1;
  }
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1.5rem;
    font-weight: 700;
  }
  
  p {
    font-size: 1.25rem;
    max-width: 800px;
    margin: 0 auto;
    opacity: 0.9;
  }
  
  @media (max-width: 768px) {
    padding: 3rem 0;
    
    h1 {
      font-size: 2.25rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

// Project showcase styling
const ProjectShowcase = styled.div`
  background-color: var(--light);
  border-radius: var(--border-radius-lg);
  padding: 3rem;
  text-align: center;
  max-width: 900px;
  margin: 2rem auto 4rem;
  box-shadow: var(--shadow-sm);
  
  h3 {
    color: var(--primary);
    margin-bottom: 1rem;
    font-size: 1.75rem;
  }
  
  p {
    color: var(--dark);
    font-size: 1.1rem;
    margin-bottom: 1.5rem;
  }
  
  .project-image {
    width: 100%;
    max-width: 800px;
    height: auto;
    border-radius: var(--border-radius-md);
    margin: 0 auto 2rem;
    box-shadow: var(--shadow-md);
    transition: transform 0.3s ease;
    
    &:hover {
      transform: scale(1.02);
    }
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem auto 3rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
    
    .project-image {
      margin-bottom: 1.5rem;
    }
  }
`;

// Future projects grid
const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// Placeholder project card
const ProjectCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  transition: var(--transition);
  
  &:hover {
    box-shadow: var(--shadow-md);
    transform: translateY(-5px);
  }
  
  .image {
    width: 100%;
    height: 200px;
    background-color: var(--gray);
    position: relative;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    
    .status {
      position: absolute;
      top: 1rem;
      right: 1rem;
      background-color: var(--secondary);
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: var(--border-radius-sm);
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
    }
  }
  
  .details {
    padding: 1.5rem;
    
    h3 {
      color: var(--primary);
      margin-bottom: 0.5rem;
      font-size: 1.25rem;
      display: flex;
      align-items: center;
      
      svg {
        margin-right: 0.5rem;
        color: var(--secondary);
      }
    }
    
    p {
      color: var(--dark);
      margin-bottom: 1rem;
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }
`;

// Title section for the future projects
const ProjectsSectionTitle = styled.div`
  text-align: center;
  margin: 4rem 0 2rem;
  
  h2 {
    color: var(--primary);
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    color: var(--gray);
    max-width: 600px;
    margin: 0 auto;
  }
  
  @media (max-width: 768px) {
    margin: 3rem 0 1.5rem;
    
    h2 {
      font-size: 1.75rem;
    }
  }
`;

const LandDevelopmentPage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <Hero>
        <div className="container">
          <h1>Land Development</h1>
          <p>Developing future communities with quality, vision, and long-term value.</p>
        </div>
      </Hero>
      
      <Section>
        <div className="container">
          {/* Project Showcase Section */}
          <ProjectShowcase>
            <h3>Upcoming Development Projects</h3>
            <img 
              src="/images/McEnt.png" 
              alt="McEntire Place Development" 
              className="project-image" 
              loading="lazy"
            />
            <p>Shoffner Homes is currently preparing new land development projects. Details coming soon.</p>
            <Button as={Link} to="/contact" variant="secondary">Contact Us for Updates</Button>
          </ProjectShowcase>
          
          {/* Future Projects Section */}
          <ProjectsSectionTitle>
            <h2>Future Developments</h2>
            <p>This section will showcase our upcoming land development projects</p>
          </ProjectsSectionTitle>
          
          {/* Placeholder Grid for Future Projects */}
          <ProjectsGrid>
            {/* Center Oaks Development */}
            <ProjectCard>
              <div className="image" style={{ 
                backgroundImage: 'url(/images/McLots.png)',
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundColor: '#f8f9fa'
              }}>
                <div className="status">Planning Phase</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Center Oaks</h3>
                <p>This thoughtfully planned community will feature 15 to 20 large 1-acre homesites nestled in a peaceful, natural setting. Homes will range from 1,900 to 2,300 sq ft, blending modern comfort with timeless craftsmanship — perfect for families seeking room to grow and breath.</p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Request Information</Button>
              </div>
            </ProjectCard>
            
            {/* Future Project Card */}
            <ProjectCard>
              <div className="image">
                <div className="status">Coming Soon</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Future Development</h3>
                <p>We have additional land development projects in the pipeline. Join our mailing list to be the first to know about upcoming communities and opportunities.</p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Stay Updated</Button>
              </div>
            </ProjectCard>
            
            {/* Future Project Card with CDMap */}
            <ProjectCard>
              <div className="image" style={{ 
                backgroundImage: 'url(/images/CDMap.png)',
                backgroundSize: 'cover',
                backgroundColor: 'transparent'
              }}>
                <div className="status">Future Project</div>
              </div>
              <div className="details">
                <h3><FaMapMarkerAlt /> Custom Development</h3>
                <p>Have land that needs developing? We partner with landowners to create thoughtfully designed communities that preserve natural beauty while maximizing value.</p>
                <Button as={Link} to="/contact" variant="outline" fullWidth>Discuss Your Project</Button>
              </div>
            </ProjectCard>
          </ProjectsGrid>
        </div>
      </Section>
    </Layout>
  );
};

export default LandDevelopmentPage;